/**
 * パン操作パッド（FR-A11Y・WCAG 2.1.1／2.5.1）。
 *
 * 拡大中の表示位置の移動は、これまでドラッグ（＝経路をなぞる単一ポインタ操作）と
 * ピンチでしか行えなかった。2.1.1 はキーボードでの操作手段を、2.5.1 は**経路を
 * なぞらない単一ポインタの手段**を要求する。キーだけでは 2.5.1 を満たせないため、
 * 押すだけで動く操作子をここで用意する（キー側は ui/input.ts の Shift＋矢印）。
 *
 * 等倍では動かす先が無いので隠す（拡大中だけ現れる）。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { ViewerState } from '../core/viewer';
import type { Translate } from '../i18n/i18n';
import type { ViewerApi } from './api';
import { ICONS } from './icons';

/**
 * 1 回の移動量（誌面の短辺に対する割合）。固定 px にしないのは、埋め込み枠の
 * 大きさで「1 回押したときの手応え」が変わってしまうため。
 */
const PAN_STEP_RATIO = 0.25;

/** 方向 → 見たい向き。値は panBy の符号（誌面を引く向き＝視点は逆へ動く）。 */
const DIRECTIONS = [
  { id: 'up', label: 'pan.up', dx: 0, dy: 1 },
  { id: 'down', label: 'pan.down', dx: 0, dy: -1 },
  { id: 'left', label: 'pan.left', dx: 1, dy: 0 },
  { id: 'right', label: 'pan.right', dx: -1, dy: 0 },
] as const;

export class PanPad {
  readonly el: HTMLElement;

  constructor(
    doc: Document,
    registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly host: HTMLElement,
    translate: Translate,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('panpad');
    this.el.hidden = true;

    for (const direction of DIRECTIONS) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = `${cls('button')} ${cls('panpad-button')} ${cls(`panpad-button--${direction.id}`)}`;
      button.setAttribute('aria-label', translate(direction.label));
      button.title = translate(direction.label);
      // アイコンは装飾（aria-hidden）。上下は左右の山形を CSS で回して使う。
      button.innerHTML = ICONS[direction.dx === 0 ? 'next' : direction.dx > 0 ? 'prev' : 'next'] ?? '';
      registry.listen(button, 'click', () => this.pan(direction.dx, direction.dy));
      this.el.appendChild(button);
    }
  }

  /** 誌面の短辺を基準に 1 歩ぶん動かす。 */
  private pan(dx: number, dy: number): void {
    const step = Math.round(Math.min(this.host.clientWidth, this.host.clientHeight) * PAN_STEP_RATIO);
    if (step <= 0) return;
    this.api.panBy(dx * step, dy * step);
  }

  /** 拡大中だけ出す（等倍では動かす先が無い）。 */
  update(state: ViewerState): void {
    this.el.hidden = state.zoom <= 1;
  }

  /** ルートを生成前の状態へ戻すため、自分の DOM を外す（listener は registry の担当）。 */
  dispose(): void {
    this.el.remove();
  }
}
